import { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import ReactDOM from 'react-dom'
import { RootState } from 'redux/config'
import { getToasts, updateToastAsRead } from 'redux/reducers/toasts'
import { MfaBanner } from 'components/MfaBanner/MfaBanner'
import { Logger } from 'utils/logger'

interface ToastChildrenProps {
  list: RootState['toasts']['list']
  /** Toasts are actually messages, so we use message id */
  handleClose: (messageGuid: string) => void
}

// Match by english or spanish translations
const rootTextPattern = /what do you want to do\?|¿qué quieres hacer\?/i

const ToastChildren: React.FC<ToastChildrenProps> = ({ list, handleClose }) => {
  return (
    <div>
      {list.length > 0 && (
        <MfaBanner
          content={list[0].body}
          onClose={() => handleClose(list[0].guid)}
          variant='legacy'
          profileId={list[0].profileId}
          messageId={list[0].guid}
        />
      )}
      {/*
        // For handling multiple messages
        {list.map((toast, index) => (
          <MfaBanner
            key={index}
            content={toast.body}
            onClose={() => handleClose(toast.guid)}
            variant='legacy'
          />
        ))}
      */}
    </div>
  )
}

/**
 * @returns the element that will appear before the container for toasts
 */
const getPreviousElementToContainer = () => {
  const h2s = document.getElementsByTagName('h2')
  for (let i = 0; i < h2s.length; i++) {
    const el = h2s.item(i)
    if (el && rootTextPattern.test(el.innerHTML)) {
      return el
    }
  }

  // We could throw an error here and use try/catch, but this makes it simpler to use in our `useEffect` function
  return null
}

/**
 * Renders toasts into the appropriate location on the page
 * This component expects the page element is already rendered. If it is not, this component will fail to render.
 * Note that only a single toast/banner is currently supported, due to accessibility concerns with multiple, identical close buttons
 */
export const DisplayToasts: React.FC<{}> = () => {
  const { list, status } = useSelector(getToasts)
  const dispatch = useDispatch()

  // Generate a div container
  const container = useMemo(() => {
    const el = document.createElement('div')
    // To get styling id must start with react-global-header
    el.setAttribute('id', 'react-global-header-banners')
    return el
  }, [])

  // Mount div container
  useEffect(() => {
    const previousElement = getPreviousElementToContainer()
    if (!previousElement) {
      Logger.error('Previous element does not exist. Unable to set root container, unable to display toasts')
    }
    previousElement?.after(container)
    return () => {
      try {
        previousElement?.removeChild(container)
      } catch {
        // Do nothing - we don't care if this fails because it means that the container has already been removed
      }
    }
  }, [container])

  const toastProps = {
    list: list.filter(message => !message.read),
    handleClose: (messageGuid: string) => dispatch(updateToastAsRead(messageGuid))
  }
  return ReactDOM.createPortal((
    (status === 'finished' && container) ? <ToastChildren {...toastProps} /> : <></>
  ), container)
}
